
/**
 * Utility functions for image upload dialog
 */

/**
 * Converts an image file to WebP format
 * @param file The original image file
 * @returns A Promise that resolves to a new File object in WebP format
 */
export const convertToWebP = async (file: File): Promise<File> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }
          
          // Draw the image on the canvas
          ctx.drawImage(img, 0, 0);
          
          // Convert to WebP
          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error('Failed to convert image to WebP'));
              return;
            }
            
            // Create a new File object with the WebP blob
            const webpFile = new File([blob], `${file.name.split('.')[0]}.webp`, {
              type: 'image/webp',
              lastModified: Date.now()
            });
            
            resolve(webpFile);
          }, 'image/webp', 0.85); // 0.85 is a good quality/size balance
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = event.target?.result as string;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

/**
 * Validates an image file for size and type
 * @param file The file to validate
 * @param maxSize Maximum allowed file size in bytes
 * @returns An error message if validation fails, null otherwise
 */
export const validateImageFile = (file: File, maxSize: number): string | null => {
  // Check if file is an image
  if (!file.type.startsWith('image/')) {
    return "Please select an image file (JPEG, PNG, GIF, etc.)";
  }
  
  // Check file size
  if (file.size > maxSize) {
    return `Maximum file size is ${Math.round(maxSize / (1024 * 1024))}MB`;
  }
  
  return null;
};

/**
 * Creates an image preview URL
 * @param file The image file
 * @returns A Promise that resolves to a data URL for the image
 */
export const createImagePreview = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target?.result as string);
    reader.onerror = () => reject(new Error('Failed to create preview'));
    reader.readAsDataURL(file);
  });
};
